import { useCallback } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

// Konfigurace pro různé typy importů
const innomoticsQuoteImportConfig = { 
    startRow: 6, 
    columns: { 
        itemSequence: 0, quantity: 1, productName: 2, description: 3, 
        opDiscount: 6, listPrice: 7, weight: 8, deliveryTime: 11 
    }, 
    stopCondition: (row) => !row || row.length < 3 || !row[2] 
};

const innomoticsCartImportConfig = {
    startRow: 1,
    columns: {
        articleNumber: 0,       // A: Article Number
        quantity: 1,            // B: Quantity
        commodityCode: 6,       // G: Commodity Code (HS Code)
        countryOfOrigin: 7,     // H: Country of Origin
        weight: 8,              // I: Weight
        unitPrice: 10,          // K: Unit Price (List Price)
        deliveryDate: 4,        // E: Exp. Delivery Date (CSV: yyyy-MM-dd)
        discount: 14,           // O: Discount
        description: 15         // P: Product Description
    },
    stopCondition: (row) => !row || !row[0]
};

const siemensImportConfig = { 
    itemSelector: 'LineItem', 
    fields: { 
        itemSequence: 'LineItemNo', productCode: 'MLFB', description: 'Product > Description', 
        quantity: 'Quantity', listPrice: 'Pricing > ProductPriceInformations > ListPrice > Value', 
        optionCodes: 'Product > MLFBOptionCodes', weight: 'Product > NetWeight', 
        hsCode: 'Product > CommodityCode', deliveryTime: 'Product > DeliveryTime', 
        origin: 'Product > CountryOfOrigin' 
    } 
};

// Pomocné funkce
const parseSiemensProductCode = (fullCode, optionCodesFromTag) => {
    if (typeof fullCode !== 'string' || !fullCode) { return { productName: fullCode, optionsCode: optionCodesFromTag || null }; }
    if (optionCodesFromTag) { const cleanedProductName = fullCode.endsWith('-Z') ? fullCode.slice(0, -2) + '-Z' : fullCode; return { productName: cleanedProductName, optionsCode: optionCodesFromTag }; }
    const separator = '-Z';
    const separatorIndex = fullCode.indexOf(separator);
    if (separatorIndex !== -1) { const productNameEndIndex = separatorIndex + separator.length; const productName = fullCode.substring(0, productNameEndIndex); const optionsCode = fullCode.substring(productNameEndIndex); return { productName, optionsCode: optionsCode || null };
    } else { return { productName: fullCode, optionsCode: null }; }
};

const parseAndConvertPercent = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') { return Math.abs(value); }
    if (typeof value === 'string') { 
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(',', '.'); 
        const num = parseFloat(cleaned); 
        return isNaN(num) ? 0 : Math.abs(num);
    }
    return 0;
};

const parseInnomoticsQuoteDiscount = (value) => {
    if (value == null) return 0;
    const num = parseFloat(String(value).replace(',', '.'));
    if (isNaN(num)) { return 0; }
    if (num > 0 && num < 1) { return num * 100; }
    return num;
};

const parseFormattedNumber = (value) => {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        // 1. Odstraní vše kromě čísel, tečky a čárky
        const cleaned = value.replace(/[^0-9.,-]/g, '');

        // 2. Najde pozici posledního výskytu tečky a čárky
        const lastDot = cleaned.lastIndexOf('.');
        const lastComma = cleaned.lastIndexOf(',');

        let normalized;

        // 3. Rozhodne, který oddělovač je desetinný (ten více vpravo)
        if (lastComma > lastDot) {
            // Čárka je desetinný oddělovač (evropský formát, např. "10.420,00")
            // Odstraníme všechny tečky (oddělovače tisíců) a čárku nahradíme tečkou.
            normalized = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
            // Tečka je desetinný oddělovač (americký formát, např. "10,420.00")
            // nebo oddělovače nejsou vůbec použity.
            // Stačí odstranit všechny čárky (oddělovače tisíců).
            normalized = cleaned.replace(/,/g, '');
        }

        const num = parseFloat(normalized);
        return isNaN(num) ? 0 : num;
    }
    return 0;
};

const getFileNameWithoutExtension = (fileName) => {
    if (typeof fileName !== 'string') return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return fileName;
    return fileName.substring(0, lastDotIndex);
};

function calculateWorkingDays(startDate, endDate) {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    start.setHours(12, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(12, 0, 0, 0);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) { return null; }
    let count = 0;
    const curDate = new Date(start.getTime());
    while (curDate < end) {
        const dayOfWeek = curDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { count++; }
        curDate.setDate(curDate.getDate() + 1);
    }
    const endDayOfWeek = end.getDay();
    if (endDayOfWeek !== 0 && endDayOfWeek !== 6) { count++; }
    return count > 0 ? count -1 : 0;
}

// Hlavní hook
export const useQuoteImporters = (setRowData, rowData, headerData, quoteId) => {
    
    const handleInnomoticsQuoteImport = useCallback((file) => {
        if (!file) return;
        const sourceName = getFileNameWithoutExtension(file.name);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false });
                const newItems = [];
                const cfg = innomoticsQuoteImportConfig;
                for (let i = cfg.startRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (cfg.stopCondition(row)) break;
                    if (rowData.length + newItems.length >= 150) { toast.warn("Import stopped. The quote has reached the maximum limit of 150 items."); break; }
                    
                    const { productName, optionsCode } = parseSiemensProductCode(row[cfg.columns.productName]);
                    const quantity = parseFloat(row[cfg.columns.quantity]);
                    const listPrice = parseFloat(String(row[cfg.columns.listPrice] || '0').replace(',', '.'));
                    if (!productName || isNaN(quantity) || isNaN(listPrice)) continue;

                    let itemSequence = parseInt(row[cfg.columns.itemSequence], 10);
                    if (isNaN(itemSequence)) {
                        itemSequence = (rowData.length + newItems.length + 1);
                    }
                    const opDiscountValue = parseInnomoticsQuoteDiscount(row[cfg.columns.opDiscount]);
                    const deliveryTimeValue = row[cfg.columns.deliveryTime];
                    const deliveryTime = deliveryTimeValue ? parseInt(deliveryTimeValue, 10) : null;

                    const newItem = {
                        ROWID: `imported_${Date.now()}_${i}`, CRM_Quote_ID: headerData?.CRM_Quote_ID || quoteId, Quote_Header_RowID: headerData?.ROWID || null, CRM_Item_ID: null, CRM_Product_Id: null,
                        Item_Sequence: itemSequence,
                        Product_Name: productName, Options_Code: optionsCode, Description: row[cfg.columns.description] || '',
                        Quantity: quantity, List_Price: listPrice, OP_Discount: opDiscountValue, RP_Discount: 0, CP_Discount: 0,
                        Weight: parseFloat(String(row[cfg.columns.weight] || '0').replace(',', '.')), 
                        Delivery_Time: isNaN(deliveryTime) ? null : deliveryTime,
                        Source: sourceName,
                        AsCost_Unit: 0, 
                        OP_AsCosts: 0
                    };
                    newItems.push(newItem);
                }
                if (newItems.length > 0) { setRowData(prev => [...prev, ...newItems]); toast.success(`${newItems.length} items imported from Innomotics Quotation.`); } else { toast.warn('No valid items found to import.'); }
            } catch (e) { toast.error("Error processing file."); console.error(e); }
        };
        reader.readAsBinaryString(file);
    }, [headerData, quoteId, rowData, setRowData]);

const handleInnomoticsCartImport = useCallback((file) => {
        if (!file) return;
        const sourceName = getFileNameWithoutExtension(file.name);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, blankrows: false, cellDates: true, raw: false });
                const newItems = [];
                const cfg = innomoticsCartImportConfig;
                const today = new Date();

                for (let i = cfg.startRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (cfg.stopCondition(row)) break;
                    if (rowData.length + newItems.length >= 150) { toast.warn("Import stopped. The quote has reached the maximum limit of 150 items."); break; }

                    const { productName, optionsCode } = parseSiemensProductCode(row[cfg.columns.articleNumber]);
                    
                    let deliveryTime = null;
                    let deliveryDateValue = row[cfg.columns.deliveryDate];

                    if (deliveryDateValue instanceof Date) {
                        deliveryTime = calculateWorkingDays(today, deliveryDateValue);
                    } 
                    else if (typeof deliveryDateValue === 'string' && deliveryDateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        const parts = deliveryDateValue.split('-').map(part => parseInt(part, 10));
                        const deliveryDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
                        deliveryTime = calculateWorkingDays(today, deliveryDate);
                    }
                    
                    // --- ZMĚNA ZDE: Návrat k jednoduchému zpracování slevy ---
                    const newItem = {
                        ROWID: `imported_cart_${Date.now()}_${i}`, CRM_Quote_ID: headerData?.CRM_Quote_ID || quoteId, Quote_Header_RowID: headerData?.ROWID || null, CRM_Item_ID: null, CRM_Product_Id: null,
                        Item_Sequence: (rowData.length + newItems.length + 1),
                        Product_Name: productName, Options_Code: optionsCode, 
                        Description: row[cfg.columns.description] || '',
                        Quantity: parseFloat(row[cfg.columns.quantity] || '1'), 
                        List_Price: parseFormattedNumber(row[cfg.columns.unitPrice]),
                        OP_Discount: parseAndConvertPercent(row[cfg.columns.discount]), // Bere se natvrdo ze sloupce O
                        RP_Discount: 0, CP_Discount: 0,
                        Weight: parseFormattedNumber(row[cfg.columns.weight]),
                        HS_Code: row[cfg.columns.commodityCode] || null,
                        Origin: (row[cfg.columns.countryOfOrigin] || '').substring(0, 2),
                        Delivery_Time: deliveryTime,
                        Source: sourceName,
                        AsCost_Unit: 0, 
                        OP_AsCosts: 0
                    };
                    newItems.push(newItem);
                }
                if (newItems.length > 0) { setRowData(prev => [...prev, ...newItems]); toast.success(`${newItems.length} items imported from Innomotics Cart.`); } else { toast.warn('No valid items found to import.'); }
            } catch (e) { toast.error("Error processing file."); console.error(e); }
        };
        reader.readAsBinaryString(file);
    }, [headerData, quoteId, rowData, setRowData]);

    const handleSiemensImport = useCallback((file) => {
        if (!file) return;
        const sourceName = getFileNameWithoutExtension(file.name);
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const xmlString = evt.target.result;
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(xmlString, "application/xml");
                if (xmlDoc.getElementsByTagName("parsererror").length > 0) { toast.error("Invalid XML file format."); return; }
                const querySelectorText = (baseElement, selector) => { const query = selector.split(' > ').join(' > *|'); const node = baseElement.querySelector('*|' + query); return node?.textContent?.trim() || null; }
                const newItems = [];
                const cfg = siemensImportConfig;
                
                const xmlItems = xmlDoc.querySelectorAll(`*|${cfg.itemSelector}`);

                for (let i = 0; i < xmlItems.length; i++) {
                    const xmlItem = xmlItems[i];
                    if (rowData.length + newItems.length >= 150) { toast.warn("Import stopped. Limit of 150 items reached."); break; }
                    
                    const mlfb = querySelectorText(xmlItem, cfg.fields.productCode);
                    if (!mlfb) continue;

                    const optionCodesFromTag = querySelectorText(xmlItem, cfg.fields.optionCodes);
                    const { productName, optionsCode } = parseSiemensProductCode(mlfb, optionCodesFromTag);
                    
                    let opDiscountValue = 0;
                    const allDiscountsBlocks = xmlItem.querySelectorAll('*|Discounts > *|Discounts');
                    for (const block of allDiscountsBlocks) {
                        const discountNameNode = block.querySelector('*|Discount');
                        const discountName = discountNameNode?.textContent?.trim();
                        if (discountName && discountName !== 'DiscountTotal') {
                            const valueNode = block.querySelector('*|Value');
                            if (valueNode) {
                                opDiscountValue = parseFloat(valueNode.textContent || '0');
                                break;
                            }
                        }
                    }

                    let quantityValue = '1';
                    for (const childNode of xmlItem.children) {
                        if (childNode.localName === 'Quantity') {
                            quantityValue = childNode.textContent || '1';
                            break;
                        }
                    }

                    const newItem = {
                        ROWID: `imported_${Date.now()}_${i}`, CRM_Quote_ID: headerData?.CRM_Quote_ID || quoteId, Quote_Header_RowID: headerData?.ROWID || null, CRM_Item_ID: null, CRM_Product_Id: null,
                        Item_Sequence: parseInt(querySelectorText(xmlItem, cfg.fields.itemSequence), 10) || (rowData.length + newItems.length + 1),
                        Product_Name: productName, Options_Code: optionsCode, Product_Code: optionsCode, Description: querySelectorText(xmlItem, cfg.fields.description) || '',
                        Quantity: parseFloat(quantityValue),
                        List_Price: parseFloat(querySelectorText(xmlItem, cfg.fields.listPrice) || '0'),
                        OP_Discount: opDiscountValue,
                        RP_Discount: 0, CP_Discount: 0,
                        Weight: parseFloat(querySelectorText(xmlItem, cfg.fields.weight) || '0'), Delivery_Time: parseInt(querySelectorText(xmlItem, cfg.fields.deliveryTime), 10) || null,
                        HS_Code: querySelectorText(xmlItem, cfg.fields.hsCode) || null, 
                        Origin: querySelectorText(xmlItem, cfg.fields.origin) || null, 
                        Source: sourceName,
                        AsCost_Unit: 0, 
                        OP_AsCosts: 0,
                    };
                    newItems.push(newItem);
                }
                if (newItems.length > 0) { setRowData(prev => [...prev, ...newItems]); toast.success(`${newItems.length} items imported from Siemens XML.`); } else { toast.warn('No valid items found to import from Siemens file.'); }
            } catch (e) { toast.error("A critical error occurred while processing the XML file."); console.error(e); }
        };
        reader.readAsText(file, 'UTF-8');
    }, [headerData, quoteId, rowData, setRowData]);

    return {
        handleInnomoticsQuoteImport,
        handleInnomoticsCartImport,
        handleSiemensImport
    };
};